import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { formatPrice } from '../../lib/utils';
import { Package, Tag, Weight, Box } from 'lucide-react';

export default function ProductViewDialog({ open, onClose, product }) {
  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Product Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product Images */}
          {product.images && product.images.length > 0 && (
            <div className="grid grid-cols-3 gap-4">
              {product.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${product.name} - ${index + 1}`}
                  className="w-full h-48 object-cover rounded-lg border"
                />
              ))}
            </div>
          )}

          {/* Product Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{product.name}</h3>
              <p className="text-gray-600 mt-2">{product.description}</p>
            </div>

            {/* Price */}
            <div className="flex items-center space-x-3">
              <span className="text-3xl font-bold text-gray-900">
                {formatPrice(product.sale_price || product.price)}
              </span>
              {product.sale_price && (
                <span className="text-xl text-gray-500 line-through">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center space-x-3">
              <Badge variant={product.is_active ? 'outline' : 'secondary'} 
                     className={product.is_active ? 'text-green-600 border-green-200' : 'text-gray-600'}>
                {product.is_active ? 'Active' : 'Inactive'}
              </Badge>
              
              {product.inventory > 0 ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  In Stock ({product.inventory})
                </Badge>
              ) : (
                <Badge variant="destructive">
                  Out of Stock
                </Badge>
              )}
            </div>

            {/* Product Details Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="flex items-center space-x-3">
                <Package className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">SKU</p>
                  <p className="font-semibold text-gray-900">{product.sku}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Box className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Inventory</p>
                  <p className="font-semibold text-gray-900">{product.inventory} units</p>
                </div>
              </div>

              {product.weight && (
                <div className="flex items-center space-x-3">
                  <Weight className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Weight</p>
                    <p className="font-semibold text-gray-900">{product.weight} kg</p>
                  </div>
                </div>
              )}

              {product.tags && product.tags.length > 0 && (
                <div className="flex items-start space-x-3 col-span-2">
                  <Tag className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {product.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Timestamps */}
            <div className="pt-4 border-t text-sm text-gray-600">
              <p>Created: {new Date(product.created_at).toLocaleString()}</p>
              <p>Last Updated: {new Date(product.updated_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
